/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/pageChangePermission.scss';

import React, {useEffect, useRef, useState} from 'react';
import {addPageChanger, PermissionCallback, removePageChanger} from "../../util/pageChanger";
import {CloseButton, voidFunction, WithBackground} from '@dvrd/dvr-controls';
import {BolmateButton} from "../controls";

interface Props {
    onAllow?: (id: number | null, callback: PermissionCallback, evt: React.MouseEvent) => void;
    onDeny?: (id: number | null, callback: PermissionCallback, evt: React.MouseEvent) => void;
    children?: string | React.ReactElement;
    changed: boolean;
    allowText?: string;
    denyText?: string;
    allowSecondary?: boolean;
    denySecondary?: boolean;
    title?: string;
}

export default function PageChangePermission(props: Props) {
    const {
        changed, children, onAllow, onDeny, allowText, denyText, allowSecondary, denySecondary, title,
    } = props;
    const [active, setActive] = useState(false);
    const permissionID = useRef<number | null>(null);
    const permissionCallback = useRef<PermissionCallback | null>(null);

    function onClose() {
        setActive(false);
        _onAnswerRequest(false);
    }

    function _onDeny(evt: React.MouseEvent) {
        setActive(false);
        if (!permissionCallback.current) return;
        if (onDeny) onDeny(permissionID.current, permissionCallback.current, evt);
        else _onAnswerRequest(false);
    }

    function _onAllow(evt: React.MouseEvent) {
        setActive(false);
        if (!permissionCallback.current) return;
        if (onAllow) onAllow(permissionID.current, permissionCallback.current, evt);
        else _onAnswerRequest(true);
    }

    function _onAnswerRequest(allow: boolean) {
        permissionCallback.current?.(permissionID.current, allow);
        permissionCallback.current = null;
    }

    function checkPageChangePermission(callback: PermissionCallback) {
        if (!changed) callback(permissionID.current, true);
        else {
            permissionCallback.current = callback;
            setActive(true);
        }
    }

    useEffect(() => {
        permissionID.current = addPageChanger(checkPageChangePermission, !children);
        return function() {
            if (permissionID.current)
                removePageChanger(permissionID.current);
        }
    }, [changed]);

    return (
        <WithBackground active={active} onClose={voidFunction} preventBackgroundClick escapeDisabled>
            <div className='page-change-permission default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{title ?? 'Onopgeslagen wijzigingen'}</label>
                    <CloseButton onClick={onClose}/>
                </div>
                <div className='body-container'>
                    {children ?? 'De wijzigingen zijn nog niet opgeslagen. Weet je zeker dat je door wilt gaan?'}
                </div>
                <div className='footer-container btn-2'>
                    <BolmateButton onClick={_onDeny} label={denyText ?? 'Nee'} secondary={denySecondary}/>
                    <BolmateButton onClick={_onAllow} label={allowText ?? 'Ja'} secondary={allowSecondary}/>
                </div>
            </div>
        </WithBackground>
    )
}

PageChangePermission.displayName = 'PageChangePermission';